import { useContext, useEffect } from 'react'
import {
    Box,
    Button,
    Group,
    Stack,
    Text,
    ThemeIcon,
    Title,
    Tooltip,
} from '@mantine/core'
import {
    Package,
    ShoppingCart,
    Trash2,
} from 'lucide-react'
import clsx from 'clsx'
import Link from 'next/link'

import { pluralWord } from '@/utils/plural-word'

import { CartContext } from '../Cart.vm'
import Footer from '../Footer'
import ListLineItems from '../shared/ListLineItems/ListLineItems'
import { pushEcommerceEvent } from '@/lib/gtm'

export const ListCartItemsTitle = () => {
    const { hasItems, itemsCount } = useContext(CartContext)
    return (
        <Stack spacing="xs">
            <Title order={2} color="gray.8">
                Mon panier
            </Title>
            {hasItems && (
                <Text
                    data-testid="cartItemsCount"
                    component="span"
                    color="gray.6"
                    size="xs"
                    weight="normal"
                >
                    <b>{itemsCount}</b> {pluralWord('article', itemsCount ?? 0)}{' '}
                    dans le panier
                </Text>
            )}

            {!hasItems && (
                <Text component="span" color="gray.6" size="xs" weight="normal">
                    Aucun article dans le panier
                </Text>
            )}
        </Stack>
    )
}

const ListCartItems = () => {
    const { hasItems, items, total, cartActor, onRemoveProductFromCart,resetCart } =
        useContext(CartContext)

    const [, send] = cartActor

    const multiMag = items ? Object.keys(items).length > 1 : false

    const clearCart = async () => {
        resetCart()        
    }

    return (
        <Box className={clsx('w-full', !hasItems && 'h-5/6')}>
            {/* If there is a cart and cart has items then show all line items */}
            {hasItems && (
                <ListLineItems
                    items={items || {}}
                    onRemove={onRemoveProductFromCart}
                />
            )}

            {!hasItems && (
                <div
                    role="listitem"
                    aria-label="Cart Line Item"
                    aria-level={2}
                    data-testid="cart-line-item"
                    className="flex flex-col items-center justify-center w-full h-full p-1 gap-y-4"
                >
                    <ThemeIcon
                        color="var(--primary)"
                        variant="light"
                        w="5rem"
                        h="5rem"
                        radius={9999}
                    >
                        <ShoppingCart className="w-6 h-6"  color='White'/>
                    </ThemeIcon>
                    <Title order={3}>Votre panier est vide</Title>
                    <Text color="gray.6">
                        Trouvez votre prochain coup de coeur
                    </Text>
                    <Link href="/">
                        <Button size="xs">Parcourir</Button>
                    </Link>
                </div>
            )}
            {hasItems && (
                <>
                    <Stack
                            className="items-end text-right"
                            spacing={0}
                            pb="xs"
                        >
                        </Stack>

                        <div className="flex items-center justify-end gap-2">
                            <Tooltip label="Vider le panier">
                                <Button
                                    aria-label="Vider le panier"
                                    onClick={clearCart}
                                    size="xs"
                                    variant="subtle"
                                    color="red"
                                    leftIcon={<Trash2 className="w-4 h-4" />}
                                >
                                    Vider le panier
                                </Button>
                            </Tooltip>
                        </div>
                </>
            )}
            {/* Cart footer */}
            <Footer>
                {hasItems && !multiMag && (
                    <>
                        <Stack
                            className="items-end text-right"
                            spacing={0}
                            pb="xs"
                        >
                            <Text c="gray.6">Total des articles</Text>
                            <Group className="gap-0">
                                <Title order={3} color="gray.8">
                                    {total}
                                </Title>
                                <Text className="text-[12px]" c="gray.7">
                                    *
                                </Text>
                            </Group>
                        </Stack>
                        <Button
                            onClick={() => send({ type: 'NEXT' })}
                            data-testid="start-checkout"
                            
                            leftIcon={<Package className="w-4 h-4" />}
                        >
                            Passer la commande
                        </Button>
                        <Text className="text-[10px]" c="gray.5">
                            * Le montant n'inclus pas les frais de livraison
                        </Text>
                    </>
                )}

                {hasItems && multiMag && (
                    <>
                        <Stack
                            className="items-end text-right"
                            spacing={0}
                            pb="xs"
                        >
                            <Text c="gray.6">Total des articles</Text>
                            <Group className="gap-0">
                                <Title order={3} color="gray.8">
                                    {total}
                                </Title>
                                <Text className="text-[12px]" c="gray.7">
                                    *
                                </Text>
                            </Group>
                        </Stack>
                        <Button
                            disabled
                            data-testid="start-checkout"
                       
                            leftIcon={<Package className="w-4 h-4" />}
                        >
                            Passer la commande
                        </Button>
                        <Text className="text-[14px]" c="red.5">
                            * Les commandes multi-magasins ne sont pas encore
                            disponibles. Pour poursuivre, merci de passer une
                            commande par magasin.
                        </Text>
                    </>
                )}
            </Footer>
        </Box>
    )
}

const CompoundListCartItems = {
    Title: ListCartItemsTitle,
    View: ListCartItems,
}

export default CompoundListCartItems
